import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "components/modal";
import { Button } from "components/button/button.tsx";
import { useSession, useUser } from '@clerk/clerk-react';
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Textarea, Spinner, Avatar, Select, Spacer, SelectItem, Tab, Tabs } from "@heroui/react";
import {
    AlertQuestion,
    ArrowUp,
    Document,
    Email,
    Search
} from "assets/icons";
import ella from './ellaAIKayleeUp.png';

interface ChatModalProps {
    isOpen: boolean;
    onOpenChange: () => void;
}

interface Message {
    sender: 'user' | 'ella';
    content: string;
}

enum EllaActions {
    VRAAG = "VRAAG",
    EMAIL = "EMAIL",
    REPLY_EMAIL = "REPLY_EMAIL",
    GENEREER_DOCUMENT = "GENEREER_DOCUMENT",
    SCRAPE = "SCRAPE",
    HELPDESK_QA = "HELPDESK_QA"
}

const actionLoadingMessages = {
    [EllaActions.VRAAG]: "Ella is looking it up...",
    [EllaActions.EMAIL]: "Ella is writing the email...",
    [EllaActions.GENEREER_DOCUMENT]: "Ella is generating your document...",
    [EllaActions.SCRAPE]: "Ella is scraping...",
    [EllaActions.REPLY_EMAIL]: "Ella is formulating a response to the email",
    [EllaActions.HELPDESK_QA]: "Ella is searching the Yally Q&A"
};

// DUTCH
// const actionLoadingMessages = {
//   [EllaActions.VRAAG]: "Ella is aan het opzoeken...",
//   [EllaActions.EMAIL]: "Ella is de e-mail aan het schrijven...",
//   [EllaActions.GENEREER_DOCUMENT]: "Ella is je document aan het genereren...",
//   [EllaActions.SCRAPE]: "Ella is aan het scrapen...",
//   [EllaActions.REPLY_EMAIL]: "Ella is een antwoord op de e-mail aan het formuleren",
//   [EllaActions.HELPDESK_QA]: "Ella doorzoekt de Yally Q&A"
// };

const KayleeModal: React.FC<ChatModalProps> = ({ isOpen, onOpenChange }) => {
    const { session } = useSession();
    const { user } = useUser();
    const [message, setMessage] = useState('');
    const [chatLog, setChatLog] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [typingMessage, setTypingMessage] = useState<Message | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [talkingAboutFiles, setTalkingAboutFiles] = useState(false);
    const [selectedAction, setSelectedAction] = useState<EllaActions>(EllaActions.VRAAG);
    const [emails] = useState<{ id: string, subject: string, sender: string }[]>([]);
    const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
    const [isMounted, setIsMounted] = useState(true); // Add this line
    const [stopTyping, setStopTyping] = useState(false);

    const handleActionSelect = (action: EllaActions) => {
        setStopTyping(true);
        setSelectedAction(action);

        // Clear the chat log
        setChatLog([]);

        setStopTyping(false);

        // Simulate typing the new message based on the selected action
        const actionMessage = getActionMessage(action);
        simulateTyping(actionMessage, "ella");
    };

    useEffect(() => {
        setIsMounted(true); // Set to true on mount

        return () => {
            setIsMounted(false); // Set to false on unmount
        };
    }, []);

    // useEffect(() => {
    //     (async () => {
    //         try {
    //             const response = await fetchWithAuth(`${API_URL}/graph/emails/${user?.id}`, {}, session);
    //             const data = await response.json();
    //             if (!isMounted) {
    //                 return;
    //             }
    //             setEmails(data);
    //         } catch (error) {
    //             console.error('Error fetching emails:', error);
    //         }
    //     })();
    // }, [user, session]);


    useEffect(() => {
        if (isOpen) {
            // Logic when the modal is opened
            setStopTyping(false);
            setChatLog([]);
            setSelectedAction(EllaActions.VRAAG);
            // const greetingContent = getGreetingMessage(user?.firstName || "");
            // simulateTyping(greetingContent, "ella");
        }
    }, [isOpen, user]);


    useEffect(() => {
        if (!isOpen) {
            // Logic when the modal is closed
            setStopTyping(true);
            setSelectedAction(EllaActions.VRAAG);
            setChatLog([]);
            setTalkingAboutFiles(false);  // Reset talkingAboutFiles to false when modal is closed

            // Call the endpoint to end the session when the modal is closed
            const clearEllaCache = async () => {
                fetchWithAuth(`${API_URL}/kaylee/end-session`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        initiator: user?.id,
                    }),
                }, session);
            }

            clearEllaCache();

            // also clear the google cache
            const clearGoogleCache = async () => {
                fetchWithAuth(`${API_URL}/ai/chat/ask`, {
                    method: 'POST',
                    headers: {
                        'Authorization': (await session?.getToken()) as string,
                        'x-session-id': (await session?.id) as string,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ question: 'exit' }),
                }, session);
            }

            clearGoogleCache();

            if (!isMounted) {
                return;
            }
        }
    }, [isOpen, user]);


    const formatMessage = (message: string): string => {
        console.log(message);

        // Ensure capitalized words are on a new line
        let formattedMessage = message.replace(/\n/g, '<br />');

        return formattedMessage.trim();
    };


    const simulateTyping = (fullMessage: string, sender: 'user' | 'ella', callback?: () => void) => {
        setTypingMessage({ sender, content: '' });
        let idx = 0;

        const typeNextLetter = () => {
            if (idx < fullMessage.length && !stopTyping) {
                setTypingMessage({ sender, content: fullMessage.substring(0, idx + 1) });
                idx++;
                setTimeout(typeNextLetter, 15); // increase or decrease speed
            } else if (!stopTyping) {
                setTypingMessage(null);
                setChatLog(prevLog => [...prevLog, { sender, content: fullMessage }]);
                if (callback) callback();
            }
        };

        typeNextLetter();
    };

    const getActionMessage = (action: EllaActions): string => {
        switch (action) {
            case EllaActions.VRAAG:
                return "How can I assist you with your question?";
            case EllaActions.EMAIL:
                return "Which email should I write for you? I will draft it in your mailbox.";
            case EllaActions.GENEREER_DOCUMENT:
                return "What is the content of the document you want to generate? You can also choose the context above for me to consider.";
            case EllaActions.SCRAPE:
                return "Upload the file you want to scrape information from and choose what you want to do with it. I can do various things for you, like reading business cards, building files, technical files (like EPCs, and so on)."
            case EllaActions.HELPDESK_QA:
                return "Paste a question that the tenant has asked and I will search the Yally Helpdesk Q&A for you to find the appropriate answer.";
            default:
                return "";
        }
    }

    //   const getActionMessage = (action: EllaActions): string => {
    //     switch (action) {
    //       case EllaActions.VRAAG:
    //         return "Met welke vraag kan ik je helpen?";
    //       case EllaActions.EMAIL:
    //         return "Welke e-mail zal ik voor je schrijven? Ik zet deze klaar in je concepten in je mailbox.";
    //       case EllaActions.GENEREER_DOCUMENT:
    //         return "Wat is de inhoud van het document dat je wil genereren? Je kan hierboven ook de context kiezen waar ik rekening mee moet houden.";
    //       case EllaActions.SCRAPE:
    //         return "Laad het bestand op waar je informatie uit wil scrapen en kies wat je ermee wil doen. Ik kan vanalles voor je doen, zoals visitekaartjes uitlezen, gebouwfiches, technische fiches (zoals EPCs, enzoverder)."
    //       case EllaActions.HELPDESK_QA:
    //         return "Plak hier een vraag die de huurder gesteld heeft en ik doorzoek de Yally Helpdesk Q&A voor jou om het gepaste antwoord te vinden."
    //       default:
    //         return "";
    //     }
    //   }

    const sendMessage = async () => {
        // Directly add user message without typing effect
        setChatLog(prevLog => [...prevLog, { sender: 'user', content: message }]);

        setIsLoading(true);

        // Check if there is a selected file
        if (selectedFile) {
            // Create a FormData object to handle file upload
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('message', message);
            formData.append('initiator', JSON.stringify({ id: user?.id, fullName: user?.fullName }) || '');
            formData.append('action', String(selectedAction))
            formData.append('extended', String(talkingAboutFiles));

            try {
                const response = await fetchWithAuth(`${API_URL}/kaylee/ask-file`, {
                    method: 'POST',
                    body: formData,
                }, session);

                const data = await response.json();

                if (!isMounted) {
                    return;
                }

                const formattedReply = formatMessage(data.reply);
                simulateTyping(formattedReply, 'ella');
            } catch (error) {
                console.error("Error sending message:", error);
                simulateTyping("Er is een fout opgetreden tijdens het verwerken van uw verzoek.", 'ella');  // You can adjust this error message
            } finally {
                setIsLoading(false);
                setMessage('');
                setSelectedFile(null); // Clear selected file
            }

        } else {
            // If no file selected, proceed as normal
            const fetchData = async () => {

                let endpointURL = `${API_URL}/kaylee/ask`; // Default endpoint
                let headers: any = {
                    'Content-Type': 'application/json',
                };
                let body = JSON.stringify({
                    message: message,
                    initiator: { id: user?.id, fullName: user?.fullName },
                    action: selectedAction,
                    extended: talkingAboutFiles,
                    emailId: selectedEmailId,
                });

                if (selectedAction === EllaActions.HELPDESK_QA) {
                    endpointURL = `${API_URL}/ai/chat/ask`;
                    headers = {
                        ...headers,
                        'Authorization': (await session?.getToken()) as string,
                        'x-session-id': (await session?.id) as string,
                    };
                    body = JSON.stringify({ question: message });
                }

                try {
                    const response = await fetchWithAuth(endpointURL, {
                        method: 'POST',
                        headers: headers,
                        body: body,
                    }, session);

                    const data = await response.json();

                    if (!isMounted) {
                        return;
                    }

                    if (selectedAction === EllaActions.HELPDESK_QA) {
                        return formatMessage(data.answer);
                    } else {
                        return formatMessage(data.reply);
                    }

                } catch (error) {
                    console.error("Error sending message:", error);
                    return "Er is een fout opgetreden tijdens het verwerken van uw verzoek.";  // You can adjust this error message
                }
            };


            const fetchPromise = fetchData();

            // Wait for 2 seconds, then simulate Ella's typing
            setTimeout(() => {
                simulateTyping("I'm on it, give me a few seconds.", 'ella', async () => {
                    const fetchedReply = await fetchPromise;
                    simulateTyping(fetchedReply as string, "ella");
                    setIsLoading(false);
                    setMessage('');
                });
                // simulateTyping("Ik ga ermee aan de slag, binnen enkele ogenblikken ben ik klaar.", 'ella', async () => {
                //     const fetchedReply = await fetchPromise;
                //     simulateTyping(fetchedReply as string, "ella");
                //     setIsLoading(false);
                //     setMessage('');
                // });
            }, 2000);
        }
    };


    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setTalkingAboutFiles(true);

            // simulateTyping(`Wat wilt u graag weten over ${file.name}?`, 'ella');
            simulateTyping(`What would you like to know about ${file.name}?`, 'ella');
        }
    };

    const handleEmailSelection = async (emailId: any) => {
        // Set the selected email ID
        setSelectedEmailId(emailId);

        // Simulate Ella's typing
        simulateTyping("Ik zal een antwoord voor je voorbereiden voor deze mail. Welke stijl van antwoord wil je? Je kan me een richting geven zoals: 'reageer positief, reageer negatief maar probeer constructief te zijn of formuleer je bedenkingen' of ik kan ook zelf een aanzet doen.", 'ella');
        setSelectedAction(EllaActions.REPLY_EMAIL);
    };
    return (
        <Modal size='full' className='max-w-212.5 max-h-[70vh] rounded-md!' isOpen={isOpen} onOpenChange={() => {
            setChatLog([]);
            onOpenChange();
        }}>
            <ModalContent className="wider-modal">
                <ModalHeader className='flex flex-col items-center justify-center'>
                    <Avatar size="md" src={ella} />
                    {/* <p className='font-normal'>Ik ben Ella, jouw Yally AI assistent</p> */}
                    <p className='font-normal'>I'm Ella, your Yally AI assistant</p>
                </ModalHeader>
                <ModalBody style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    <Tabs onSelectionChange={(key) => handleActionSelect(key as EllaActions)} variant='underlined'
                        aria-label="Options" className='flex justify-center items-center'>
                        <Tab key={EllaActions.VRAAG} title={
                            <div className='flex items-center gap-2 pb-2'>
                                <AlertQuestion />
                                {/* <p>Stel vraag</p> */}
                                <p>Ask question</p>

                            </div>
                        }>
                        </Tab>
                        <Tab key={EllaActions.EMAIL} title={
                            <div className='flex items-center gap-2 pb-2'>
                                <Email />
                                {/* <p>Stuur e-mail</p> */}
                                <p>Send e-mail</p>
                            </div>
                        }>

                        </Tab>
                        <Tab key={EllaActions.GENEREER_DOCUMENT} title={
                            <div className='flex items-center gap-2 pb-2'>
                                <Document />
                                <p>Generate document</p>
                                {/* <p>Genereer document</p> */}
                            </div>
                        }>
                        </Tab>
                        <Tab key={EllaActions.HELPDESK_QA} title={
                            <div className='flex items-center gap-2 pb-2'>
                                <Search />
                                <p>Search Helpdesk Q&A</p>
                                {/* <p>Doorzoek helpdesk Q&A</p> */}
                            </div>
                        }>
                        </Tab>
                    </Tabs>
                    <div className="chatLog mt-8">
                        {chatLog.map((msg, idx) => (
                            <div key={idx} className={`flex gap-2 mb-4 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} items-end`}>
                                {msg.sender === "ella" && <Avatar size="md" className="min-w-[5%]" src={ella}></Avatar>}
                                <div style={msg.sender === 'ella' ? { background: 'rgba(49, 64, 59, 0.10)' } : { background: '#EA8162' }} className='p-2 rounded-md'>
                                    <p className={msg.sender === 'ella' ? 'font-bold capitalize text-neutral-primary' : 'font-bold capitalize text-white'}>{msg.sender === "ella" ? msg.sender : user?.firstName}</p>
                                    <p className={`text-align-${msg.sender === 'user' ? 'right text-white' : 'left text-neutral-primary'} max-w-[500px] overflow-auto break-words`}
                                        dangerouslySetInnerHTML={{ __html: msg.content }}></p>
                                </div>
                                {msg.sender === "user" && <Avatar size="md" className="min-w-[5%] self-end"
                                    src={user?.imageUrl || "defaultUserImageUrl"} />}
                            </div>
                        ))}
                        {typingMessage && (
                            <div className={`flex gap-2 mb-4 ${typingMessage.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {typingMessage.sender === 'ella' && <Avatar className="min-w-[5%]" size="md" src={ella}></Avatar>}
                                <p className={`text-align-${typingMessage.sender === 'user' ? 'right' : 'left'}`}
                                    dangerouslySetInnerHTML={{ __html: typingMessage.content }}></p>

                                {typingMessage.sender === 'user' &&
                                    <Avatar className="min-w-[5%] self-start" size="md" src={user?.imageUrl || "defaultUserImageUrl"} />}
                            </div>
                        )}
                    </div>
                    {isLoading && <Spinner label={actionLoadingMessages[selectedAction]} color="warning" />}
                    {selectedAction === EllaActions.REPLY_EMAIL && (
                        <div className="mb-2 text-warning">
                            Give Ella some instructions to formulate a reply
                            {/* Geef Ella enkele instructies om het antwoord op de geselecteerde e-mail voor te bereiden. */}
                        </div>
                    )}

                    {selectedAction === EllaActions.EMAIL ? (
                        <Select
                            placeholder="Or let Ella formulate an answer to an existing e-mail in your mailbox"
                            onChange={(e) => handleEmailSelection(e.target.value)}
                        >
                            {emails.map(email => (
                                <SelectItem key={email.id}>
                                    Van: {email.sender}: {email.subject}
                                </SelectItem>
                            ))}
                        </Select>

                    )
                        : selectedAction == EllaActions.SCRAPE ?
                            <Select placeholder="What should I do with the scraped informations?">
                                <SelectItem key="create-contact">Create new contact</SelectItem>
                                <SelectItem key="create-contact">Create new acquisition file</SelectItem>
                                <SelectItem key="create-contact">Send content to colleagues</SelectItem>
                            </Select> : ""}

                    <Spacer y={4} />
                    {/* {(selectedAction == EllaActions.EMAIL || selectedAction == EllaActions.GENEREER_DOCUMENT) ? (
            <>
              <div className="flex">
                <div className="flex p-1 min-w-[30%]">
                  <User/>
                  <p className='pl-2 pr-16'>Gericht aan:</p>
                </div>
                <div className="grow">
                  <SearchComponent type="regular" label="Kies contact" resourceFilter={[ResourceType.CONTACT]}
                                   onSelect={(resource) => setAddressee(resource)}/>
                </div>
              </div>
              <div className="flex">
                <div className="flex p-1 min-w-[30%]">
                  <Building/>
                  <p className='pl-2 pr-16'>Gebouwcontext:</p>
                </div>
                <div className="grow">
                  <SearchComponent type="regular" label="Kies gebouw" resourceFilter={[ResourceType.BUILDING]}
                                   onSelect={(resource) => setBuildingContext(resource)}/>
                </div>
              </div>
            </>) : ""} */}
                    <div className='flex gap-2'>
                        {selectedAction == EllaActions.VRAAG || selectedAction == EllaActions.SCRAPE ? (
                            <div className="pt-2">
                                <Button onClick={() => document.getElementById('fileInputKaylee')?.click()} isIconOnly
                                    variant='bordered' radius='full'>
                                    <Document />
                                </Button>

                                <input
                                    type="file"
                                    id="fileInputKaylee"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={(e) => handleFileChange(e)}
                                />
                            </div>
                        ) : ''}
                        {selectedAction != EllaActions.SCRAPE ? <Textarea
                            type="text"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            // placeholder={
                            //     selectedAction == EllaActions.EMAIL ? "Schrijf concept mail" :
                            //         selectedAction == EllaActions.VRAAG ? "Stel vraag" :
                            //             selectedAction == EllaActions.GENEREER_DOCUMENT ? "Genereer document" :
                            //                 selectedAction == EllaActions.REPLY_EMAIL ? "Formuleer antwoord" :
                            //                     selectedAction == EllaActions.HELPDESK_QA ? "Doorzoek Q&A" : ""
                            // }
                            placeholder={
                                selectedAction == EllaActions.EMAIL ? "Write concept mail" :
                                    selectedAction == EllaActions.VRAAG ? "Ask question" :
                                        selectedAction == EllaActions.GENEREER_DOCUMENT ? "Generate document" :
                                            selectedAction == EllaActions.REPLY_EMAIL ? "Formulate answer" :
                                                selectedAction == EllaActions.HELPDESK_QA ? "Search through Q&A" : ""
                            }
                            disabled={isLoading}
                        /> : ""}
                        <div className="pt-2">
                            <Button radius='full' className='bg-moss-primary text-white' isIconOnly onClick={sendMessage}
                                disabled={isLoading}>
                                <ArrowUp />
                            </Button>
                        </div>
                    </div>

                </ModalBody>
                <ModalFooter>

                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default KayleeModal;
